import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { headers } from '../../../actions/defaults';
import { API_URL } from '../../../environment';
import { playPhoneCallChime } from './phoneCallChime';

export const PHONE_CALL_LOOKUP_RETRIES = 2;
export const PHONE_CALL_LOOKUP_RETRY_DELAY_MS = 1000;

// Tracks phone dial-in participants for a video session. Each entry has a
// status: 'pending' (lookup not finished yet), 'resolved' (caller identified)
// or 'unknown' (lookup failed after all retries).
//
// onPhoneCallConnected always returns true so the session hook subscribes to
// the stream right away: a phone caller must stay audible even when the
// caller lookup fails (the caller hears the whole session).
export function useVoiceCalls() {
  const [voiceCalls, setVoiceCalls] = useState([]);
  // connectionId -> retry timer id. Doubles as the synchronous dedup set and
  // the "still connected" check for in-flight lookups.
  const trackedRef = useRef(new Map());

  const updateCall = useCallback((connectionId, update) => {
    setVoiceCalls(calls =>
      calls.map(call =>
        call.video_session_connection_id === connectionId
          ? { ...call, ...update }
          : call,
      ),
    );
  }, []);

  const onPhoneCallConnected = useCallback(
    async connectionId => {
      const tracked = trackedRef.current;
      if (tracked.has(connectionId)) return true;
      tracked.set(connectionId, null);
      playPhoneCallChime('join');

      setVoiceCalls(calls =>
        calls.some(call => call.video_session_connection_id === connectionId)
          ? calls
          : [
              ...calls,
              {
                status: 'pending',
                id: `phone:${connectionId}`,
                video_session_connection_id: connectionId,
              },
            ],
      );

      const lookup = async attempt => {
        if (!tracked.has(connectionId)) return;
        try {
          const response = await axios.get(
            `${API_URL}/vonage_videos/voice_call_link?connection_id=${encodeURIComponent(
              connectionId,
            )}`,
            headers(),
          );
          if (!tracked.has(connectionId)) return;
          // The caller can be linked without a number (e.g. withheld caller
          // ID), so there is nothing to show: treat it as unidentified.
          updateCall(connectionId, {
            ...response.data,
            video_session_connection_id: connectionId,
            status: response.data?.from_number_last4 ? 'resolved' : 'unknown',
          });
        } catch {
          if (!tracked.has(connectionId)) return;
          if (attempt < PHONE_CALL_LOOKUP_RETRIES) {
            tracked.set(
              connectionId,
              setTimeout(
                () => lookup(attempt + 1),
                PHONE_CALL_LOOKUP_RETRY_DELAY_MS,
              ),
            );
            return;
          }
          tracked.set(connectionId, null);
          updateCall(connectionId, { status: 'unknown' });
        }
      };

      lookup(0);
      return true;
    },
    [updateCall],
  );

  const onPhoneCallDisconnected = useCallback(connectionId => {
    const tracked = trackedRef.current;
    const wasTracked = tracked.has(connectionId);
    clearTimeout(tracked.get(connectionId));
    tracked.delete(connectionId);
    if (wasTracked) playPhoneCallChime('leave');
    setVoiceCalls(calls =>
      calls.filter(call => call.video_session_connection_id !== connectionId),
    );
  }, []);

  useEffect(() => {
    const tracked = trackedRef.current;
    return () => {
      tracked.forEach(timer => clearTimeout(timer));
      tracked.clear();
    };
  }, []);

  return { voiceCalls, onPhoneCallConnected, onPhoneCallDisconnected };
}
